import { UMEDS_MARKETING } from '@/routes/route-paths';
import { validateEscriptToken } from '@montugroup/circuit-shared/utils/escript';
import { Button, Checkbox, Link, Stack, Typography } from '@montugroup/design-system';

import { StyledTokenInputWrapper } from '@/pages/escript/landing/SubmitSectionStyles';
import FormControlLabel from '@mui/material/FormControlLabel';
import { styled } from '@mui/material/styles';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { TokenInputField } from './TokenInputField';
import type { SubmitTokenUnion, TokenInputWithAgreementProps } from './types';

const StyledFormControlLabel = styled(FormControlLabel)(() => ({
  alignItems: 'flex-start',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(6),
}));

export function TokenInputWithAgreement({
  openTokenDialog,
  setOpenTokenDialog,
  handleTokenSubmit,
  onTokenInvalid,
}: TokenInputWithAgreementProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<SubmitTokenUnion>();

  const isAgreementChecked = useWatch({ name: 'agreementCheckbox' });
  const tokenValue = useWatch({ name: 'tokenInput' });

  return (
    <form onSubmit={handleSubmit(handleTokenSubmit, onTokenInvalid)}>
      <StyledTokenInputWrapper spacing={10}>
        <Controller
          name="tokenInput"
          control={control}
          rules={{
            required: 'Please enter your script token',
            validate: validateEscriptToken,
          }}
          render={({ field, fieldState }) => (
            <TokenInputField
              field={field}
              fieldState={fieldState}
              formErrors={errors}
              openTokenDialog={openTokenDialog}
              setOpenTokenDialog={setOpenTokenDialog}
            />
          )}
        />

        <Stack spacing={4.5}>
          <Controller
            name="agreementCheckbox"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <StyledFormControlLabel
                control={
                  <Checkbox
                    data-tracking-id="escript_onboarding_agreement"
                    checked={value}
                    onChange={onChange}
                    inputRef={ref}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" sx={(theme) => ({ paddingTop: theme.spacing(2) })}>
                    I understand that my prescription details will be treated in accordance with the uMeds{' '}
                    <Link href={UMEDS_MARKETING.PRIVACY_POLICY} data-tracking-id="escript_onboarding_privacy_policy">
                      <Typography
                        variant="body2"
                        component="span"
                        sx={(theme) => ({ color: theme.palette.primary.main })}
                      >
                        privacy policy
                      </Typography>
                    </Link>
                    .
                  </Typography>
                }
              />
            )}
          />

          <StyledButton
            data-tracking-id="escript_onboarding_submit_token"
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={!isAgreementChecked || !tokenValue?.trim()}
            size="large"
          >
            Submit
          </StyledButton>
        </Stack>
      </StyledTokenInputWrapper>
    </form>
  );
}
